/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { ListImperativeAPI } from 'react-window';
import { dateUtils } from '@opengeoweb/shared';
import { EARTH_MEAN_RADIUS, MAX_PROXIMITY_RADIUS } from './Soundings/constants';
import type { FeaturesFromBE } from '../api/edr-api/types';

export const getDisplayValue = (
  instanceId: string,
  stringFormat = 'ISO',
): string => {
  const instanceAsDate = dateUtils.isoStringToDate(instanceId);
  if (instanceAsDate) {
    return stringFormat === 'ISO'
      ? dateUtils.dateToIsoString(instanceAsDate)!
      : dateUtils.dateToString(instanceAsDate, stringFormat, true)!;
  }
  return instanceId;
};

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;
export const kmToRad = (km: number): number => km / EARTH_MEAN_RADIUS;

/**
 * Great-circle distance in km between two [lon, lat] points (haversine).
 */
export const haversineDistanceKm = (
  from: [number, number],
  to: [number, number],
): number => {
  const fromLatRad = degToRad(from[1]);
  const toLatRad = degToRad(to[1]);
  const deltaLatRad = toLatRad - fromLatRad;
  const deltaLonRad = degToRad(to[0] - from[0]);
  const haversineOfCentralAngle =
    Math.sin(deltaLatRad / 2) ** 2 +
    Math.cos(fromLatRad) * Math.cos(toLatRad) * Math.sin(deltaLonRad / 2) ** 2;
  return (
    2 *
    EARTH_MEAN_RADIUS *
    Math.asin(Math.min(1, Math.sqrt(haversineOfCentralAngle)))
  );
};

const COMPASS_8 = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/**
 * Initial bearing in degrees from `from` toward `to` (0° = north, clockwise).
 */
export const bearingDeg = (
  from: [number, number],
  to: [number, number],
): number => {
  const lat1 = degToRad(from[1]);
  const lat2 = degToRad(to[1]);
  const dLon = degToRad(to[0] - from[0]);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

export const bearingToCompass8 = (deg: number): string =>
  COMPASS_8[Math.round(deg / 45) % 8];

/**
 * Format [lon, lat] as "60.24°N 24.15°E".
 */
export const formatLatLon = ([lon, lat]: [number, number]): string => {
  const latStr = `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? 'N' : 'S'}`;
  const lonStr = `${Math.abs(lon).toFixed(2)}°${lon >= 0 ? 'E' : 'W'}`;
  return `${latStr} ${lonStr}`;
};

/**
 * Format a distance in km using m below 1 km, one decimal below 10 km,
 * whole km otherwise.
 */
export const formatDistanceKm = (km: number): string => {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  if (km < 10) {
    return `${km.toFixed(1)} km`;
  }
  return `${Math.round(km)} km`;
};

/**
 * Find nearest point to the target location in the given features, if it is within the specified maximum distance.
 * Uses (mid-latitude modified) equirectangular distance approximation which is not accurate for long
 * distances, but works for finding nearby (< 100 km) locations.
 *
 * @param targetLocation target location the user has clicked
 * @param pointFeatures features to search through
 * @param maxDistanceKm maximum distance to consider
 * @returns feature nearest to the target location or null if none are within the maximum distance
 */
export const findNearestLocationFeature = (
  pointFeatures: FeaturesFromBE[],
  targetLocation: [number, number],
  maxDistanceKm = MAX_PROXIMITY_RADIUS,
): FeaturesFromBE | null => {
  if (!pointFeatures.length) {
    return null;
  }

  // Convert max distance from km to radians and square it for comparison with squared distance.
  // Squared distance is used to avoid square root calculation in distance formula,
  // since we only need relative distances to find the nearest point.
  // Basically this is based on the fact that if distance d1 is less than distance d2, then d1^2 is also less than d2^2, so we can compare squared distances without losing correctness.
  const maxSphericalDistanceSquared = kmToRad(maxDistanceKm) ** 2;

  const targetLon = degToRad(targetLocation[0]);
  const targetLat = degToRad(targetLocation[1]);

  const nearestFeature =
    pointFeatures.reduce<{
      feature: FeaturesFromBE;
      sphericalDistanceSquared: number;
    } | null>((nearestResult, currentFeature) => {
      if (currentFeature.geometry.type !== 'Point') {
        return nearestResult;
      }

      const [featureLonDeg, featureLatDeg] = currentFeature.geometry
        .coordinates as [number, number];
      const featureLat = degToRad(featureLatDeg);
      const dLat = featureLat - targetLat;
      if (dLat * dLat > maxSphericalDistanceSquared) {
        return nearestResult;
      }

      const midLat = 0.5 * (targetLat + featureLat);
      const featureLon = degToRad(featureLonDeg);
      const dLon = featureLon - targetLon;
      const dLonConv = dLon * Math.cos(midLat);
      const sphericalDistanceSquared = dLonConv * dLonConv + dLat * dLat;

      if (sphericalDistanceSquared > maxSphericalDistanceSquared) {
        return nearestResult;
      }
      if (
        !nearestResult ||
        sphericalDistanceSquared < nearestResult.sphericalDistanceSquared
      ) {
        return {
          feature: currentFeature,
          sphericalDistanceSquared,
        };
      }
      return nearestResult;
    }, null)?.feature ?? null;

  return nearestFeature;
};

export const useScrollToBottomOnDatasetChange = <Row>(
  listRef: React.RefObject<ListImperativeAPI | null>,
  rows: readonly Row[],
): void => {
  React.useEffect(() => {
    if (rows.length === 0) {
      return undefined;
    }
    const lastIndex = rows.length - 1;
    const id = requestAnimationFrame(() => {
      listRef.current?.scrollToRow({ index: lastIndex, align: 'end' });
    });
    return (): void => cancelAnimationFrame(id);
  }, [listRef, rows]);
};
